import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Container, 
  TextField, 
  MenuItem, 
  Box, 
  CircularProgress, 
  Slider, 
  Button, 
  InputAdornment,
  Collapse,
  Paper,
  Dialog, 
  DialogContent,
  useMediaQuery,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import ProductDetails from './ProductDetails';
import { 
  translateValue, 
  categoryMap,
  watchMaterialMap,
  jewelryMaterialMap,
  collectionMap,
  stoneTypeMap,
  stoneCutMap,
  stoneClarityMap,
  conditionMap,
  locationMap
} from '../../utils/productMappings';

const WATCH_BRANDS_MODELS_BRACELETS = {
  "Rolex": {
    "Oyster Perpetual": { "diameters": [28, 31, 34, 36, 41] },
    "Datejust": { "diameters": [26, 31, 36, 41] },
    "Day-Date": { "diameters": [36, 40] },
    "Submariner": { "diameters": [41] },
    "Sea-Dweller": { "diameters": [43] },
    "Deepsea": { "diameters": [44] },
    "Explorer": { "diameters": [36, 39] },
    "GMT-Master II": { "diameters": [40] },
    "Yacht-Master": { "diameters": [37, 40, 42] }
  },
  "Chopard": {
    "Happy Sport": { "diameters": [30, 36, 40] },
    "Mille Miglia": { "diameters": [39, 42, 44] },
    "L.U.C": { "diameters": [39, 40, 42] },
    "Alpine Eagle": { "diameters": [36, 41, 44] }
  },
  "Cartier": {
    "Tank Must": { "diameters": [29, 34] },
    "Tank Française": { "diameters": [25, 30, 37] },
    "Santos de Cartier": { "diameters": [35, 40, 48] },
    "Ballon Bleu": { "diameters": [28, 33, 36, 40, 42] },
    "Pasha de Cartier": { "diameters": [35, 41, 42] }
  },
  "Audemars Piguet": {
    "Royal Oak": { "diameters": [33, 34, 37, 39, 41] },
    "Royal Oak Offshore": { "diameters": [42, 44] },
    "Code 11.59": { "diameters": [38, 41] }
  },
  "Patek Philippe": {
    "Nautilus": { "diameters": [35, 40, 42] },
    "Aquanaut": { "diameters": [36, 39, 42] },
    "Calatrava": { "diameters": [36, 37, 38, 39] },
    "Gondolo": { "diameters": [30, 32, 36, 38, 40] }
  }
};

const WATCH_BRANDS = Object.keys(WATCH_BRANDS_MODELS_BRACELETS);
const WATCH_DIAMETERS = [...new Set(Object.values(WATCH_BRANDS_MODELS_BRACELETS).flatMap(models => Object.values(models).flatMap(details => details.diameters)))].sort((a, b) => a - b);
const JEWELRY_COLLECTIONS = Object.keys(collectionMap);
const MATERIALS = { ...watchMaterialMap, ...jewelryMaterialMap };
const STONE_TYPES = Object.keys(stoneTypeMap);
const STONE_CUTS = Object.keys(stoneCutMap);
const STONE_CLARITY = Object.keys(stoneClarityMap);

// Stała określająca liczbę produktów na stronę
const PRODUCTS_PER_PAGE = 12;

// Niezawodna funkcja do formatowania ceny
const formatPrice = (price) => {
  if (price === null || price === undefined) return 'B/D';
  const priceString = String(price);
  return priceString.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    searchTerm: '',
    category: 'all',
    brand: 'all',
    model: 'all',
    bracelet: 'all',
    price: [0, 1000000],
    location: 'all',
    condition: 'all',
    material: 'all',
    collection: 'all',
    stoneType: 'all',
    stoneCut: 'all',
    stoneClarity: 'all',
    certificate: 'all',
    diameter: 'all',
    size: '',
  });
  const [sortBy, setSortBy] = useState('updatedAt_desc');
  const [totalProductCount, setTotalProductCount] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const loader = useRef(null);

  const resetAndFetch = () => {
    setLastVisible(null);
    setProducts([]);
    setHasMore(true);
    fetchProducts();
    fetchTotalProductCount();
  };

  const fetchProducts = useCallback(async (isLoadMore = false) => {
    if (loading) return;
    setLoading(true);

    try {
      let q = query(collection(db, 'products'));

      // Server-side filters
      if (filters.searchTerm) {
        const searchTermLower = filters.searchTerm.toLowerCase();
        // Musimy użyć wielu zapytań i połączyć ich wyniki,
        // ponieważ Firestore nie obsługuje zapytań OR w jednym zapytaniu
        const queries = [
          // Wyszukiwanie po searchableKeywords (jeśli istnieją)
          query(collection(db, 'products'), where('searchableKeywords', 'array-contains', searchTermLower)),
          // Wyszukiwanie po SKU
          query(collection(db, 'products'), where('sku', '>=', searchTermLower), where('sku', '<=', searchTermLower + '\uf8ff')),
          // Wyszukiwanie po numerze referencyjnym
          query(collection(db, 'products'), where('details.reference_number', '>=', searchTermLower), where('details.reference_number', '<=', searchTermLower + '\uf8ff')),
        ];
        
        // Zegarki - dodatkowe zapytanie dla marki
        if (WATCH_BRANDS.some(brand => brand.toLowerCase().includes(searchTermLower))) {
          queries.push(query(collection(db, 'products'), 
            where('category', '==', 'watch'),
            where('details.brand', '>=', searchTermLower), 
            where('details.brand', '<=', searchTermLower + '\uf8ff')
          ));
        }
        
        // Zegarki - dodatkowe zapytanie dla modelu
        // Sprawdzamy wszystkie modele ze wszystkich marek
        const allWatchModels = [];
        Object.keys(WATCH_BRANDS_MODELS_BRACELETS).forEach(brand => {
          Object.keys(WATCH_BRANDS_MODELS_BRACELETS[brand]).forEach(model => {
            allWatchModels.push(model);
          });
        });
        
        if (allWatchModels.some(model => model.toLowerCase().includes(searchTermLower))) {
          queries.push(query(collection(db, 'products'), 
            where('category', '==', 'watch'),
            where('details.model', '>=', searchTermLower), 
            where('details.model', '<=', searchTermLower + '\uf8ff')
          ));
        }
        
        // Biżuteria - dodatkowe zapytanie dla nazwy biżuterii
        queries.push(query(collection(db, 'products'), 
          where('category', '==', 'jewelry'),
          where('details.name', '>=', searchTermLower), 
          where('details.name', '<=', searchTermLower + '\uf8ff')
        ));
        
        // Wykonujemy wszystkie zapytania równolegle
        setLoading(true);
        Promise.all(queries.map(q => getDocs(q)))
          .then(snapshots => {
            const uniqueProducts = {};
            // Łączymy wyniki, usuwając duplikaty
            snapshots.forEach(snapshot => {
              snapshot.forEach(doc => {
                const productData = doc.data();
                uniqueProducts[doc.id] = { id: doc.id, ...productData };
              });
            });
            
            // Konwertujemy obiekt na tablicę
            const productsArray = Object.values(uniqueProducts);
            
            // Stosujemy pozostałe filtry klienckie (np. kategoria, stan, itp.)
            let filteredProducts = productsArray.filter(p => !['sold', 'sale_pending'].includes(p.status)).filter(doesProductMatchFilters);
            
            // Nie sortujemy ponownie po stronie klienta, ponieważ sortowanie jest już wykonane przez Firestore
            // To zapewnia spójność sortowania dla wszystkich zapytań
            console.log(`Sortowanie ${sortBy} jest obsługiwane przez zapytanie Firestore`);
            
            // DEBUG: W przypadku braku danych zwracanych przez serwer, sprawdźmy jakie parametry były użyte
            if (filteredProducts.length === 0) {
              console.log('Brak wyników - sprawdź parametry zapytania:');
              console.log('Sortowanie:', sortBy);
              console.log('Filtry:', filters);
            }
            // Diagnostyka sortowania - sprawdzenie jakie dane otrzymujemy
            console.log('====== DIAGNOSTYKA SORTOWANIA ======');
            console.log(`Tryb sortowania: ${sortBy}`);
            console.log(`Liczba produktów przed sortowaniem: ${filteredProducts.length}`);
            
            // Sprawdźmy pierwsze kilka produktów aby zobaczyć ich ceny i daty
            if (filteredProducts.length > 0) {
              console.log('Przykładowe produkty:');
              filteredProducts.slice(0, Math.min(5, filteredProducts.length)).forEach((product, index) => {
                console.log(`Produkt ${index + 1}:`);
                console.log(`  ID: ${product.id}`);
                console.log(`  Nazwa: ${product.name || product.jewelry_name || 'Brak nazwy'}`);
                console.log(`  Cena: ${product.price || 'Brak ceny'} (typ: ${typeof product.price})`);
                
                // Sprawdź format daty updatedAt
                const updatedAt = product.updatedAt;
                if (updatedAt) {
                  console.log(`  updatedAt istnieje: ${updatedAt}`);
                  console.log(`  updatedAt jest Timestamp: ${updatedAt.toDate ? 'tak' : 'nie'}`);
                  if (updatedAt.toDate) {
                    console.log(`  Data: ${updatedAt.toDate().toLocaleString()}`);
                  }
                } else {
                  console.log(`  updatedAt: brak`);
                }
              });
            }
            
            setProducts(filteredProducts);
            setLoading(false);
            
            // Jeśli potrzebujemy paginacji, możemy zapisać ostatni widoczny dokument
            if (filteredProducts.length > 0) {
              setLastVisible(filteredProducts[filteredProducts.length - 1]);
            } else {
              setLastVisible(null);
            }
            setHasMore(filteredProducts.length === PRODUCTS_PER_PAGE);
          })
          .catch(error => {
            console.error("Error getting documents: ", error);
            setLoading(false);
          });
          
        // Zatrzymujemy dalsze wykonywanie głównego zapytania
        return;
      }
      if (filters.category !== 'all') {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.brand !== 'all') {
        q = query(q, where('brand', '==', filters.brand));
      }
      if (filters.model !== 'all') {
        q = query(q, where('model', '==', filters.model));
      }
      // Zawsze pokazujemy tylko produkty dostępne (status == "available")
      q = query(q, where('status', '==', 'available'));
      if (filters.location !== 'all') {
        q = query(q, where('location', '==', filters.location));
      }
      if (filters.condition !== 'all') {
        q = query(q, where('condition', '==', filters.condition));
      }
      if (filters.material !== 'all') {
        q = query(q, where('material', '==', filters.material));
      }
      if (filters.collection !== 'all' && filters.category === 'jewelry') {
        q = query(q, where('collection', '==', filters.collection));
      }
      if (filters.stoneType !== 'all' && filters.category === 'jewelry') {
        q = query(q, where('central_stone.type', '==', filters.stoneType));
      }
      if (filters.stoneCut !== 'all' && filters.category === 'jewelry') {
        q = query(q, where('central_stone.cut', '==', filters.stoneCut));
      }
      if (filters.stoneClarity !== 'all' && filters.category === 'jewelry') {
        q = query(q, where('central_stone.clarity', '==', filters.stoneClarity));
      }
      if (filters.certificate !== 'all' && filters.category === 'jewelry') {
        q = query(q, where('central_stone.has_certificate', '==', filters.certificate === 'yes'));
      }
      if (filters.diameter !== 'all' && filters.category === 'watch') {
        q = query(q, where('case_diameter', '==', parseInt(filters.diameter)));
      }

      // Price filter
      q = query(q, where('price', '>=', filters.price[0]), where('price', '<=', filters.price[1]));

      // Sorting - pojedyncze źródło prawdy (Firestore)
      if (sortBy === 'price_asc') {
        q = query(q, orderBy('price', 'asc'));
      } else if (sortBy === 'price_desc') {
        q = query(q, orderBy('price', 'desc'));
      } else if (sortBy === 'updatedAt_asc') {
        // 'Najstarsze' - od najstarszej daty aktualizacji do najnowszej
        q = query(q, orderBy('updatedAt', 'asc'));
      } else {
        // 'Najnowsze' - od najnowszej daty aktualizacji do najstarszej
        // Domyślnie: sortBy === 'updatedAt_desc'
        q = query(q, orderBy('updatedAt', 'desc'));
      }

      if (isLoadMore && lastVisible) {
        q = query(q, startAfter(lastVisible), limit(12));
      } else {
        q = query(q, limit(12));
      }

      const documentSnapshots = await getDocs(q);
      const newProducts = documentSnapshots.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lastDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];

      setLastVisible(lastDoc || null);
      setHasMore(documentSnapshots.docs.length === 12);

      // Zapobiegamy duplikatom przez sprawdzenie ID produktów
      if (isLoadMore) {
        // Zbiór wszystkich ID produktów, które już mamy
        const existingIds = new Set(products.map(p => p.id));
        
        // Filtrujemy nowe produkty, usuwając duplikaty
        const uniqueNewProducts = newProducts.filter(p => !existingIds.has(p.id));
        
        setProducts([...products, ...uniqueNewProducts]);
      } else {
        setProducts(newProducts);
      }

    } catch (error) {
      console.error("Błąd podczas pobierania produktów: ", error);
      if (error.code === 'failed-precondition') {
        console.error("Błąd zapytania Firestore. Prawdopodobnie brakuje złożonego indeksu. Sprawdź konsolę przeglądarki, aby znaleźć link do jego utworzenia.");
      }
      setProducts([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, lastVisible, loading]);

  useEffect(() => {
    fetchProducts();
    fetchTotalProductCount();
  }, [filters, sortBy]);

  // Obsługa infinite scroll
  const handleObserver = useCallback((entries) => {
    const target = entries[0];
    if (target.isIntersecting && hasMore && !loading) {
      fetchProducts(true);
    }
  }, [loading, hasMore, fetchProducts]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { rootMargin: '100px' });
    if (loader.current) observer.observe(loader.current);
    return () => {
      if (loader.current) observer.unobserve(loader.current);
    };
  }, [handleObserver]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'category') {
      // Resetuj wszystkie filtry specyficzne dla kategorii przy zmianie kategorii
      setFilters(prev => ({
        ...prev,
        [name]: value,
        // Resetuj filtry zegarków jeśli zmieniono kategorię
        brand: 'all',
        model: 'all',
        bracelet: 'all',
        diameter: 'all',
        condition: 'all',
        // Resetuj filtry biżuterii jeśli zmieniono kategorię
        size: '',
        stoneType: 'all',
        stoneCut: 'all',
        stoneClarity: 'all',
        certificate: 'all',
      }));
    } else {
      setFilters(prev => ({
        ...prev, 
        [name]: value, 
        ...(name === 'brand' && { model: 'all', bracelet: 'all', diameter: 'all' }),
        ...(name === 'model' && { bracelet: 'all', diameter: 'all' })
      }));
    }
  };

  const handlePriceInputChange = (e, index) => {
    const value = e.target.value;
    const newPrice = [...filters.price];
    // Ustawia pusty string jeśli pole jest puste, w przeciwnym razie konwertuje na liczbę
    newPrice[index] = value === '' ? '' : Number(value);
    setFilters(prev => ({ ...prev, price: newPrice }));
  };

  const handlePriceChange = (event, newValue) => {
    setFilters(prev => ({ ...prev, price: newValue }));
  };

  const handleSortChange = (event) => {
    const newSortValue = event.target.value;
    console.log('Zmiana sortowania na:', newSortValue);
    setSortBy(newSortValue);
    
    // Nie używamy resetAndFetch, ponieważ może używać nieaktualnej wartości sortBy ze stanu
    setLastVisible(null);
    setProducts([]);
    setHasMore(true);
    
    // Bezpośrednio wywołujemy fetchProducts z nową wartością sortowania
    fetchProductsWithSort(newSortValue);
    fetchTotalProductCount();
  };
  
  // Nowa funkcja, która używa przekazanej wartości sortowania zamiast stanu
  const fetchProductsWithSort = async (sortValue, isLoadMore = false) => {
  if (loading) return;
  setLoading(true);
  
  try {
    let q = query(collection(db, 'products'));

    // Hardcoded status filter to exclude sold and sold_pending products
    q = query(q, where('status', 'not-in', ['sold', 'sold_pending']));
    
    // Other filters
    if (filters.category !== 'all') {
      q = query(q, where('category', '==', filters.category));
    }
    if (filters.brand !== 'all') {
      q = query(q, where('brand', '==', filters.brand));
    }
    if (filters.model !== 'all') {
      q = query(q, where('model', '==', filters.model));
    }
    if (filters.location !== 'all') {
      q = query(q, where('location', '==', filters.location));
    }
    if (filters.condition !== 'all') {
      q = query(q, where('condition', '==', filters.condition));
    }
    if (filters.material !== 'all') {
      q = query(q, where('material', '==', filters.material));
    }
    if (filters.collection !== 'all' && filters.category === 'jewelry') {
      q = query(q, where('collection', '==', filters.collection));
    }
    if (filters.stoneType !== 'all' && filters.category === 'jewelry') {
      q = query(q, where('central_stone.type', '==', filters.stoneType));
    }
    if (filters.stoneCut !== 'all' && filters.category === 'jewelry') {
      q = query(q, where('central_stone.cut', '==', filters.stoneCut));
    }
    if (filters.stoneClarity !== 'all' && filters.category === 'jewelry') {
      q = query(q, where('central_stone.clarity', '==', filters.stoneClarity));
    }
    if (filters.certificate !== 'all' && filters.category === 'jewelry') {
      q = query(q, where('central_stone.has_certificate', '==', filters.certificate === 'yes'));
    }
    if (filters.diameter !== 'all' && filters.category === 'watch') {
      q = query(q, where('case_diameter', '==', parseInt(filters.diameter)));
    }
    
    // Price filter
    q = query(q, where('price', '>=', filters.price[0]), where('price', '<=', filters.price[1]));
    
    // Sorting
    if (sortValue === 'price_asc') {
      q = query(q, orderBy('price', 'asc'));
    } else if (sortValue === 'price_desc') {
      q = query(q, orderBy('price', 'desc'));
    } else if (sortValue === 'updatedAt_asc') {
      q = query(q, orderBy('updatedAt', 'asc'));
    } else {
      q = query(q, orderBy('updatedAt', 'desc'));
    }
    
    if (isLoadMore && lastVisible) {
      q = query(q, startAfter(lastVisible), limit(PRODUCTS_PER_PAGE));
    } else {
      q = query(q, limit(PRODUCTS_PER_PAGE));
    }
    
    const querySnapshot = await getDocs(q);
    const fetchedProducts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    if (isLoadMore) {
      setProducts(prev => [...prev, ...fetchedProducts]);
    } else {
      setProducts(fetchedProducts);
    }
    
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
    setLastVisible(lastDoc || null);
    setHasMore(fetchedProducts.length === PRODUCTS_PER_PAGE);

  } catch (error) {
    console.error("Error fetching products: ", error);
  } finally {
    setLoading(false);
  }
};

  const handleCardClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };
  
  // Funkcja do zliczania całkowitej liczby produktów spełniających kryteria
  const fetchTotalProductCount = async () => {
    try {
      // Przygotowanie warunków zapytania
      let conditions = [];
      
      // Zastosuj wszystkie filtry do zliczania tak samo jak przy pobieraniu produktów
      if (filters.category !== 'all') {
        conditions.push(where('category', '==', filters.category));
      }
      
      if (filters.brand !== 'all' && filters.category === 'watch') {
        conditions.push(where('details.brand', '==', filters.brand));
      }
      
      if (filters.model !== 'all' && filters.category === 'watch') {
        conditions.push(where('details.model', '==', filters.model));
      }
      
      if (filters.condition !== 'all' && filters.category === 'watch') {
        conditions.push(where('condition', '==', filters.condition));
      }
      
      if (filters.diameter !== 'all' && filters.category === 'watch') {
        conditions.push(where('details.case_size', '==', parseInt(filters.diameter)));
      }
      
      if (filters.status === 'available') {
        conditions.push(where('sold', '==', false));
      } else if (filters.status === 'sold') {
        conditions.push(where('sold', '==', true));
      }
      
      // Dodanie warunku filtrującego statusy `sold` i `sold_pending`
      conditions.push(where('status', 'not-in', ['sold', 'sale_pending']));
      
      // Tworzenie zapytania z warunkami
      const productsQuery = query(collection(db, 'products'), ...conditions);
      
      // Pobierz dane
      const querySnapshot = await getDocs(productsQuery);
      
      // Zastosuj dodatkowe filtry po stronie klienta (tam, gdzie nie możemy użyć where)
      const filteredDocs = querySnapshot.docs.filter(doc => {
        const product = { ...doc.data(), id: doc.id };
        return doesProductMatchFilters(product);
      });
      
      // Ustaw całkowitą liczbę produktów
      setTotalProductCount(filteredDocs.length);
    } catch (error) {
      console.error("Error getting total product count: ", error);
    }
  };

    const doesProductMatchFilters = (product) => {
    // Zawsze wykluczaj produkty sprzedane lub w trakcie sprzedaży
    if (['sold', 'sale_pending'].includes(product.status)) {
      return false;
    }

      // Filtrowanie po kategorii
      if (filters.category !== 'all' && product.category !== filters.category) {
        return false;
      }
      
      // Filtrowanie po marce (dla zegarków)
      if (filters.brand !== 'all' && product.category === 'watch') {
        if (!product.details || product.details.brand !== filters.brand) {
          return false;
        }
      }
      
      // Filtrowanie po modelu (dla zegarków)
      if (filters.model !== 'all' && product.category === 'watch') {
        if (!product.details || product.details.model !== filters.model) {
          return false;
        }
      }
      
      // Filtrowanie po bransolecie (dla zegarków)
      if (filters.bracelet !== 'all' && product.category === 'watch') {
        if (!product.details || product.details.bracelet !== filters.bracelet) {
          return false;
        }
      }

      // Filtrowanie po średnicy koperty (dla zegarków)
      if (filters.diameter !== 'all' && product.category === 'watch') {
        if (!product.details || product.details.case_size !== parseInt(filters.diameter)) {
          return false;
        }
      }
      
      // Filtrowanie po kolekcji (dla biżuterii)
      if (filters.collection !== 'all' && product.category === 'jewelry') {
        if (!product.details || product.details.collection !== filters.collection) {
          return false;
        }
      }
      
      // Filtrowanie po rodzaju kamienia (dla biżuterii)
      if (filters.stoneType !== 'all' && product.category === 'jewelry') {
        if (!product.details || product.details.stone_type !== filters.stoneType) {
          return false;
        }
      }
      
      // Filtrowanie po szlifie kamienia (dla biżuterii)
      if (filters.stoneCut !== 'all' && product.category === 'jewelry') {
        if (!product.details || !product.details.stones || 
            !product.details.stones.center || 
            product.details.stones.center.cut !== filters.stoneCut) {
          return false;
        }
      }
      
      // Filtrowanie po czystości kamienia (dla biżuterii)
      if (filters.stoneClarity !== 'all' && product.category === 'jewelry') {
        if (!product.details || !product.details.stones || 
            !product.details.stones.center || 
            product.details.stones.center.clarity !== filters.stoneClarity) {
          return false;
        }
      }
      
      // Filtrowanie po certyfikacie (dla biżuterii)
      if (filters.certificate !== 'all' && product.category === 'jewelry') {
        if (!product.details || !product.details.certificate || 
            product.details.certificate !== filters.certificate) {
          return false;
        }
      }
      
      // Filtrowanie po rozmiarze (dla biżuterii)
      if (filters.size && filters.size.trim() !== '' && product.category === 'jewelry') {
        if (!product.details || !product.details.size || 
            !product.details.size.toString().toLowerCase().includes(filters.size.toLowerCase())) {
          return false;
        }
      }
      
      // Filtrowanie po lokalizacji
      if (filters.location !== 'all') {
        if (!product.location || product.location !== filters.location) {
          return false;
        }
      }
      
      // Filtrowanie po zakresie cenowym
      if (filters.price && Array.isArray(filters.price) && filters.price.length === 2) {
        const price = parseFloat(product.price || 0);
        if (price < filters.price[0] || price > filters.price[1]) {
          return false;
        }
      }
      
      // Filtrowanie po stanie (dostępny/sprzedany)
      if (filters.status === 'available' && product.sold) {
        return false;
      } else if (filters.status === 'sold' && !product.sold) {
        return false;
      }
      
      // Filtrowanie po stanie produktu (nienoszony, bardzo dobry, dobry)
      if (filters.condition !== 'all') {
        if (!product.condition || product.condition !== filters.condition) {
          return false;
        }
      }
      
      // Produkt przeszedł wszystkie filtry
      return true;
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      category: 'all',
      brand: 'all',
      model: 'all',
      bracelet: 'all',
      price: [0, 1000000], // Zaktualizowany reset ceny
      location: 'all',
      condition: 'all',
      material: 'all',
      collection: 'all',
      stoneType: 'all',
      stoneCut: 'all',
      stoneClarity: 'all',
      certificate: 'all',
      diameter: 'all',
      size: '',
    });
    setSortBy('updatedAt_desc');
    resetAndFetch();
  };

  const renderFilters = () => (
    <Box sx={{ p: 3 }}>
      {/* Pole wyszukiwania */}
      <Box sx={{ mb: 3 }}>
        <TextField 
          fullWidth 
          placeholder="Szukaj po ID, nr ref." 
          variant="outlined" 
          name="searchTerm" 
          value={filters.searchTerm} 
          onChange={handleFilterChange} 
          InputProps={{ 
            startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: 'white' }} /></InputAdornment>),
            sx: {
              color: 'white',
              bgcolor: '#1a1a1a',
              '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
            }
          }}
        />
      </Box>

      {/* Sortowanie */}
      <Accordion 
        disableGutters 
        elevation={0} 
        sx={{ 
          bgcolor: '#1a1a1a', 
          color: 'white',
          border: '1px solid #333',
          '&:before': { display: 'none' },
          mb: 2
        }}
        defaultExpanded
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
          <Typography>Sortowanie</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
            <TextField
              select
              fullWidth
              name="sortBy"
              value={sortBy}
              onChange={handleSortChange}
              variant="outlined"
              InputProps={{
                sx: { 
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                }
              }}
              SelectProps={{
                IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
              }}
            >
              <MenuItem value="updatedAt_desc">Najnowsze</MenuItem>
              <MenuItem value="updatedAt_asc">Najstarsze</MenuItem>
              <MenuItem value="price_asc">Cena: od najniższej</MenuItem>
              <MenuItem value="price_desc">Cena: od najwyższej</MenuItem>
            </TextField>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Kategoria */}
          <Accordion 
            disableGutters 
            elevation={0} 
            sx={{ 
              bgcolor: '#1a1a1a', 
              color: 'white',
              border: '1px solid #333',
              '&:before': { display: 'none' },
              mb: 2
            }}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
              <Typography>Kategoria</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box 
                sx={{ 
                  border: '1px solid #333', 
                  borderRadius: 1, 
                  p: 0, 
                  bgcolor: '#1a1a1a',
                  '&:hover': { borderColor: '#555' },
                  cursor: 'pointer'
                }}
              >
                <TextField
                  select
                  fullWidth
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  variant="outlined"
                  InputProps={{
                    sx: { 
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }
                  }}
                  SelectProps={{
                    IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                  }}
                  sx={{ '.MuiSelect-icon': { color: 'white' } }}
                >
                  <MenuItem value="all">Wszystkie</MenuItem>
                  {Object.entries(categoryMap).map(([key, value]) => (
                    <MenuItem key={key} value={key}>{value}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>
          
          {/* Materiał */}
          <Accordion 
            disableGutters 
            elevation={0} 
            sx={{ 
              bgcolor: '#1a1a1a', 
              color: 'white',
              border: '1px solid #333',
              '&:before': { display: 'none' },
              mb: 2
            }}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
              <Typography>Materiał</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                <TextField
                  select
                  fullWidth
                  name="material"
                  value={filters.material}
                  onChange={handleFilterChange}
                  variant="outlined"
                  InputProps={{
                    sx: { 
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }
                  }}
                  SelectProps={{
                    IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                  }}
                >
                  <MenuItem value="all">Wszystkie</MenuItem>
                  {Object.entries(MATERIALS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Lokalizacja */}
          <Accordion 
            disableGutters 
            elevation={0} 
            sx={{ 
              bgcolor: '#1a1a1a', 
              color: 'white',
              border: '1px solid #333',
              '&:before': { display: 'none' },
              mb: 2
            }}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
              <Typography>Lokalizacja</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                <TextField
                  select
                  fullWidth
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  variant="outlined"
                  InputProps={{
                    sx: { 
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }
                  }}
                  SelectProps={{
                    IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                  }}
                >
                  <MenuItem value="all">Wszystkie</MenuItem>
                  {Object.entries(locationMap).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Zegarki - filtry specyficzne */}
          {filters.category === 'watch' && (
            <>
              {/* Stan produktu - tylko dla zegarków */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Stan produktu</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="condition"
                      value={filters.condition}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {Object.entries(conditionMap).map(([k, v]) => (
                        <MenuItem key={k} value={k}>{v}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Marka zegarka */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Marka zegarka</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="brand"
                      value={filters.brand}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {WATCH_BRANDS.map(brand => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Model zegarka */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
                disabled={filters.brand === 'all'}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Model zegarka</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="model"
                      value={filters.model}
                      onChange={handleFilterChange}
                      variant="outlined"
                      disabled={filters.brand === 'all'}
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {filters.brand !== 'all' && WATCH_BRANDS_MODELS_BRACELETS[filters.brand] && 
                        Object.keys(WATCH_BRANDS_MODELS_BRACELETS[filters.brand]).map(model => (
                          <MenuItem key={model} value={model}>{model}</MenuItem>
                        ))
                      }
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Średnica */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
                disabled={filters.brand === 'all' || filters.model === 'all'}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Średnica</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="diameter"
                      value={filters.diameter}
                      onChange={handleFilterChange}
                      variant="outlined"
                      disabled={filters.brand === 'all' || filters.model === 'all'}
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {filters.brand !== 'all' && 
                       filters.model !== 'all' && 
                       WATCH_BRANDS_MODELS_BRACELETS[filters.brand] && 
                       WATCH_BRANDS_MODELS_BRACELETS[filters.brand][filters.model] && 
                       WATCH_BRANDS_MODELS_BRACELETS[filters.brand][filters.model].diameters && 
                       WATCH_BRANDS_MODELS_BRACELETS[filters.brand][filters.model].diameters.map(d => (
                        <MenuItem key={d} value={d}>{`${d} mm`}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Biżuteria - filtry specyficzne */}
          {filters.category === 'jewelry' && (
            <>
              {/* Rozmiar biżuterii */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Rozmiar</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      fullWidth
                      name="size"
                      value={filters.size}
                      onChange={handleFilterChange}
                      placeholder="Np. 16, 17.5"
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              
              {/* Kamień */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Kamień</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="stoneType"
                      value={filters.stoneType}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {STONE_TYPES.map(t => (
                        <MenuItem key={t} value={t}>{stoneTypeMap[t]}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Szlif kamienia */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Szlif</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="stoneCut"
                      value={filters.stoneCut}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {STONE_CUTS.map(c => (
                        <MenuItem key={c} value={c}>{stoneCutMap[c]}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Czystość kamienia */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Czystość</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="stoneClarity"
                      value={filters.stoneClarity}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {STONE_CLARITY.map(c => (
                        <MenuItem key={c} value={c}>{stoneClarityMap[c]}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Kolekcja */}
              <Accordion 
                disableGutters 
                elevation={0} 
                sx={{ 
                  bgcolor: '#1a1a1a', 
                  color: 'white',
                  border: '1px solid #333',
                  '&:before': { display: 'none' },
                  mb: 2
                }}
                defaultExpanded
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                  <Typography>Kolekcja</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ border: '1px solid #333', borderRadius: 1, p: 0, bgcolor: '#1a1a1a', '&:hover': { borderColor: '#555' } }}>
                    <TextField
                      select
                      fullWidth
                      name="collection"
                      value={filters.collection}
                      onChange={handleFilterChange}
                      variant="outlined"
                      InputProps={{
                        sx: { 
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }
                      }}
                      SelectProps={{
                        IconComponent: (props) => <ExpandMoreIcon {...props} sx={{ color: 'white' }} />
                      }}
                    >
                      <MenuItem value="all">Wszystkie</MenuItem>
                      {JEWELRY_COLLECTIONS.map(c => (
                        <MenuItem key={c} value={c}>{collectionMap[c]}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Cena */}
          <Accordion 
            disableGutters 
            elevation={0} 
            sx={{ 
              bgcolor: '#1a1a1a', 
              color: 'white',
              border: '1px solid #333',
              '&:before': { display: 'none' },
              mb: 2
            }}
            defaultExpanded
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
              <Typography>Cena</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ px: 2, py: 1 }}>
                <Slider
                  value={filters.price.map(v => (v === '' ? 0 : v))}
                  onChange={handlePriceChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={Math.max(1000000, filters.price[1] === '' ? 0 : Number(filters.price[1]))} // Dynamic max
                  step={1000}
                  valueLabelFormat={(value) => `${value.toLocaleString()} PLN`}
                  sx={{
                    color: '#e0e0e0',
                    '& .MuiSlider-thumb': { backgroundColor: '#fff' },
                    '& .MuiSlider-track': { backgroundColor: '#e0e0e0' },
                    '& .MuiSlider-rail': { backgroundColor: '#555' },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <TextField
                    label="Od"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={filters.price[0]}
                    onChange={(e) => handlePriceInputChange(e, 0)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      sx: {
                        color: 'white',
                        bgcolor: '#1a1a1a',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                      }
                    }}
                    sx={{ width: '48%' }}
                  />
                  <TextField
                    label="Do"
                    variant="outlined"
                    size="small"
                    type="number"
                    value={filters.price[1]}
                    onChange={(e) => handlePriceInputChange(e, 1)}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                      sx: {
                        color: 'white',
                        bgcolor: '#1a1a1a',
                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                      }
                    }}
                    sx={{ width: '48%' }}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Przyciski akcji */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={clearFilters} 
              startIcon={<ClearIcon />}
              sx={{ 
                color: 'white', 
                borderColor: '#555',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' } 
              }}
            >
              Wyczyść filtry
            </Button>
            <Button 
              variant="contained" 
              onClick={() => fetchProducts()}
              sx={{ 
                bgcolor: '#e0e0e0', 
                color: '#1a1a1a',
                '&:hover': { bgcolor: 'white' } 
              }}
            >
              Wyszukaj
            </Button>
          </Box>
        </Box>
  );

    const renderProductCard = (product) => {

    const translateValue = (map, value) => {
      if (map && typeof map === 'object' && value in map) {
        return map[value];
      }
      return value || '';
    };

    const renderInfoField = (label, value) => (
      value ? <Typography variant="body2" color="text.secondary">{label}: {value}</Typography> : null
    );

    const renderJewelryCard = () => (
      <>
        <Typography variant="h6" component="h2" fontWeight="bold" noWrap>
        {`${translateValue(categoryMap, product.category)} ${product.jewelry_name || ''}`.trim()}
      </Typography>
        <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center">
          {renderInfoField('Materiał', translateValue(jewelryMaterialMap, product.material))}
          {product.material !== 'platinum' && renderInfoField('Próba', product.gold_fineness)}
          {renderInfoField('Waga', product.weight_grams ? `${product.weight_grams} g` : null)}
          {renderInfoField('ID', product.sku)}
          {product.location && <Typography variant="body2" color="text.secondary" noWrap>Lokalizacja: {translateValue(locationMap, product.location)}</Typography>}
        </Box>
        <Typography variant="h5" component="p" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
          {`${formatPrice(product.price)} PLN`}
        </Typography>
      </>
    );

    const renderWatchCard = () => (
      <>
        <Typography variant="h6" component="h2" fontWeight="bold" noWrap>
        {`${product.brand || ''} ${product.model || ''} ${product.case_diameter ? `${product.case_diameter} mm` : ''}`.trim()}
      </Typography>
        <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="center">
          {renderInfoField('Materiał', translateValue(watchMaterialMap, product.material))}
          {renderInfoField('Stan', translateValue(conditionMap, product.condition))}
          {renderInfoField('Średnica', product.case_diameter ? `${product.case_diameter} mm` : null)}
          {renderInfoField('Rok', product.production_year)}
          {renderInfoField('Kolor tarczy', product.dial_color)}
          {product.location && <Typography variant="body2" color="text.secondary" noWrap>Lokalizacja: {translateValue(locationMap, product.location)}</Typography>}
        </Box>
        <Typography variant="h5" component="p" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
          {`${formatPrice(product.price)} PLN`}
        </Typography>
      </>
    );

    return (
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          cursor: 'pointer', 
          bgcolor: '#000000', 
          color: 'white', 
          border: '1px solid #333',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 4px 20px 0 rgba(255, 215, 0, 0.3)'
          }
        }}
        onClick={() => handleCardClick(product)}
      >
        <CardMedia
          component="div"
          sx={{
            position: 'relative',
            paddingTop: '100%', // 1:1 Aspect Ratio
            backgroundColor: '#000000', // Czarne tło
            cursor: 'pointer'
          }}
        >
          {product.status === 'in_workshop' && (
            <Chip 
              label="W pracowni"
              color="secondary"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, fontWeight: 'bold' }}
            />
          )}
          {product.status === 'in_transfer' && (
            <Chip 
              label="W transferze"
              color="info"
              size="small"
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, fontWeight: 'bold' }}
            />
          )}
          <img 
            src={product.images && product.images.length > 0 ? 
              product.images[0] : 
              'https://via.placeholder.com/300'
            } 
            alt={product.name || ''} 
            style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute', top: 0, left: 0 }} 
          />
        </CardMedia>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
          {product.brand ? renderWatchCard() : renderJewelryCard()}
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom component="h1">Produkty</Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Panel filtrów i sortowania */}
        <Grid item xs={12}>
          <Paper sx={{ bgcolor: '#1a1a1a', color: 'white', border: '1px solid #333', borderRadius: 1, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2, borderBottom: showFilters ? '1px solid #333' : 'none' }}>
              <IconButton 
                onClick={() => setShowFilters(!showFilters)} 
                size="small"
                sx={{ mr: 1, color: 'white', border: '1px solid #555', p: 1 }}
              >
                <FilterListIcon />
              </IconButton>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Filtry i Sortowanie</Typography>
              <Typography sx={{ mr: 2 }}>Znaleziono: {totalProductCount}</Typography>
            </Box>
            <Collapse in={showFilters}>{renderFilters()}</Collapse>
          </Paper>
        </Grid>
      </Grid>
      {loading && products.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>
      ) : products.length > 0 ? (
        <Grid container spacing={isMobile ? 2 : 3}>
          {products.map((product) => (
            <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>{renderProductCard(product)}</Grid>
          ))}
        </Grid>
      ) : (
        <Typography sx={{ textAlign: 'center', my: 5 }}>Brak produktów spełniających kryteria.</Typography>
      )}
      {loading && products.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>
      )}
      <div ref={loader} />
      {selectedProduct && <ProductDetails productId={selectedProduct.id} open={isModalOpen} onClose={handleCloseModal} />}
    </Container>
  );
};

export default ProductsList;